import React, { useState } from 'react';
import { Box, Button, Chip, Modal, ModalDialog, Typography } from '@mui/joy';
import { X as CloseIcon } from '@phosphor-icons/react';
import CatalogProductSearch from './CatalogProductSearch';
import CatalogOrderPanel from './CatalogOrderPanel';
import type { ApiCartLine } from './ApiOrderCart';
import type { CatalogProduct } from '../../lib/distributorsApi';

interface Props {
  isSrs?: boolean;
  environmentLabel?: string;
  open: boolean;
  onClose: () => void;
  distributorId: number;
  distributorName: string;
  onAdd: (product: CatalogProduct) => void;
  cartLines: ApiCartLine[];
  onQuantityChange: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  maxLines: number;
  shipToNumber?: string;
  branchNumber?: string;
  branchLabel?: string;
}

/** Full-screen product browser with an editable order sidebar and mobile panel. */
export default function CatalogBrowserModal({
  open,
  onClose,
  distributorId,
  distributorName,
  onAdd,
  cartLines,
  onQuantityChange,
  onRemove,
  maxLines,
  shipToNumber,
  branchNumber,
  branchLabel,
  isSrs,
  environmentLabel,
}: Props) {
  const [showOrder, setShowOrder] = useState(false);
  const cartCount = cartLines.length;
  const full = cartCount >= maxLines;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog data-sentry-block layout="fullscreen" sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography level="h4">{distributorName} catalog</Typography>
            {environmentLabel && <Chip color="warning" size="sm" sx={{ my: 0.5 }}>{environmentLabel}</Chip>}
            <Typography level="body-sm" color="neutral">
              Add everything you need, then return to the order.
              {branchLabel ? ` Prices shown are your account's, for ${branchLabel}.` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button size="sm" variant="outlined" sx={{ display: { xs: 'inline-flex', lg: 'none' } }} aria-expanded={showOrder} aria-controls="catalog-order-panel" onClick={() => setShowOrder(value => !value)}>{showOrder ? 'Browse products' : `Your order (${cartCount})`}</Button>
            <Chip color={full ? 'warning' : 'primary'} variant="soft" size="lg">
              {cartCount} of {maxLines} lines
            </Chip>
            <Button variant="solid" onClick={onClose} startDecorator={<CloseIcon />}>
              Done
            </Button>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 340px' } }}>
        <Box sx={{ minWidth: 0, minHeight: 0, px: { xs: 2, md: 3 }, py: 2, display: { xs: showOrder ? 'none' : 'flex', lg: 'flex' }, flexDirection: 'column' }}>
          <CatalogProductSearch
            isSrs={isSrs}
            distributorId={distributorId}
            onAdd={onAdd}
            disabled={full}
            cartLines={cartLines}
            resultsHeight="100%"
            thumbnailSize={72}
            autoFocus
            shipToNumber={shipToNumber}
            branchNumber={branchNumber}
          />
        </Box>
        <Box id="catalog-order-panel" sx={{ minHeight: 0, borderLeft: { lg: '1px solid' }, borderColor: 'divider', bgcolor: 'background.level1', display: { xs: showOrder ? 'block' : 'none', lg: 'block' } }}>
          <CatalogOrderPanel lines={cartLines} onQuantityChange={onQuantityChange} onRemove={onRemove} />
        </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
